import { useEffect, useState } from 'react';

import { docIO } from '../../lib/doc-io.js';
import { useWorkspace } from '../../state/workspace.js';
import { NumberField, PropRow, SelectField } from '../ui/controls.js';

/**
 * Texture import settings of one image (`<image>.import.json`, Unity's
 * Texture Importer): filtering, wrap, mipmaps, max size and the sprite
 * pixels per unit. Shown under sprite / tilemap components; edits write the
 * sidecar file next to the image (see engine/docs/systems/2d.md).
 */
type ImportDoc = Record<string, unknown>;

const DEFAULT = '(default)';

export function TextureImportEditor({ image }: { image: string }) {
  const projectId = useWorkspace((s) => s.projectId);
  const path = `${image}.import.json`;
  const [doc, setDoc] = useState<ImportDoc | null>(null);

  useEffect(() => {
    if (!projectId || !image) return;
    let live = true;
    docIO()
      .read(projectId, path)
      .then((text) => {
        let parsed: unknown = {};
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = {};
        }
        if (live) setDoc(parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as ImportDoc) : {});
      })
      .catch(() => live && setDoc({}));
    return () => {
      live = false;
    };
  }, [projectId, image, path]);

  if (!projectId || !image || !doc) return null;

  const update = (key: string, value: unknown) => {
    const next: ImportDoc = { ...doc, format: 'prism.textureImport/1' };
    if (value === undefined) delete next[key];
    else next[key] = value;
    setDoc(next);
    void docIO().write(projectId, path, `${JSON.stringify(next, null, 2)}\n`);
  };
  const choice = (key: string, options: string[], hint: string, label: string) => (
    <PropRow label={label} hint={hint}>
      <SelectField
        value={typeof doc[key] === 'string' ? (doc[key] as string) : DEFAULT}
        options={[DEFAULT, ...options]}
        onChange={(v) => update(key, v === DEFAULT ? undefined : v)}
      />
    </PropRow>
  );

  return (
    <div className="texture-import">
      <p className="muted">Import settings of {image}</p>
      {choice('filterMode', ['point', 'bilinear', 'trilinear'], 'point = crisp pixel art; default = linear (pixel art documents: their own filter)', 'Filter Mode')}
      {choice('wrapMode', ['repeat', 'clamp', 'mirror'], 'Sampling outside the image (default repeat)', 'Wrap Mode')}
      <PropRow label="Pixels Per Unit" hint="Image pixels per world unit for sprites / tiles using it. Empty = settings.render.pixelsPerUnit.">
        <NumberField
          value={typeof doc.pixelsPerUnit === 'number' ? doc.pixelsPerUnit : undefined}
          step={1}
          min={0.01}
          onChange={(v) => update('pixelsPerUnit', v > 0 ? v : undefined)}
        />
      </PropRow>
      <PropRow label="Max Size" hint="Larger images are downscaled to fit (keeps the aspect ratio). Empty = no limit.">
        <NumberField
          value={typeof doc.maxSize === 'number' ? doc.maxSize : undefined}
          step={1}
          min={1}
          max={16384}
          integer
          onChange={(v) => update('maxSize', v >= 1 ? v : undefined)}
        />
      </PropRow>
      <PropRow label="Mipmaps" hint="Generate mipmaps (default: on unless the filter is point)">
        <SelectField
          value={typeof doc.mipmaps === 'boolean' ? (doc.mipmaps ? 'on' : 'off') : DEFAULT}
          options={[DEFAULT, 'on', 'off']}
          onChange={(v) => update('mipmaps', v === DEFAULT ? undefined : v === 'on')}
        />
      </PropRow>
    </div>
  );
}
